import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import {CplCycle} from '../../../../../model/cpl-cycle.model';
import {VerifyService} from '../../../../../services/verify.service';
import {UserVerification} from '../../../../../model/cycle-verification.model';
import {CplItem} from '../../../../../model/cpl-item.model';
import {VerificationStatus} from '../../enums/verification-status.enum';
import { CplVerificationStage } from '../../../../../model/cpl-verification-stage.model';
import { ComplianceItemHelpersService } from 'src/app/services/compliance-item-helpers/compliance-item-helpers.service';
import { DateHelpers, SessionService, User, UserService } from '@edl-development/edl-common';
import { A48HelpersService } from 'src/app/services/a48-helpers/a48-helpers.service';
import { CycleType } from '../../enums/cycle-type.enum';
import { CplItemSubType } from 'src/app/model/enum/cpl-item-sub-type.enum';
import { CplRequirement } from 'src/app/model/enum/cpl-requirement.enum';

@Component({
    templateUrl: './verify-modal.component.html',
    styleUrls: ['./verify-modal.component.scss']
})
export class VerifyModalComponent implements OnInit {
    @Input() userCycle: CplCycle;
    @Input() userCycleActions: Array<CplCycle>;
    @Input() complianceItem: CplItem;
    @Input() action?: VerificationStatus;
    @Input() canClose: boolean = true;

    @Output() onSubmissionComplete = new EventEmitter<boolean>();

    form: UntypedFormGroup;

    CanManageItem: boolean;
    IsVerifier: boolean;
    isFinalStage: boolean;
    pendingStage: CplVerificationStage;
    VerificationStatus = VerificationStatus;
    CplItemSubType = CplItemSubType;
    CplRequirement = CplRequirement;

    busy: boolean;
    user: User;

    constructor(
        public activeModal: NgbActiveModal,
        private formBuilder: UntypedFormBuilder,
        private verifyService: VerifyService,
        private userService: UserService,
        private a48HelpersService: A48HelpersService,
        private complianceItemHelpersService: ComplianceItemHelpersService,
        private sessionService: SessionService
    ) { }

    ngOnInit() {
        this.busy = true;
        this.CanManageItem = this.complianceItemHelpersService.canManageItem(this.complianceItem);
        this.IsVerifier = this.complianceItemHelpersService.isVerifier(this.complianceItem);

        if(this.isBulkAction()) this.userCycle = this.userCycleActions[0];

        if (!this.isBulkAction() && this.userCycle.VerificationStage && this.complianceItem.VerificationStages) {
            this.pendingStage = this.complianceItem.VerificationStages.find(v => v.StageID === this.userCycle.VerificationStage.StageID);
            this.isFinalStage = this.pendingStage && !this.complianceItem.VerificationStages.find(v => v.Order > this.pendingStage?.Order);
        }

        if (!this.isBulkAction()) {
            this.userService.getAccountByUserId(this.userCycle.UserID)
                .subscribe((result: User) =>  {
                    this.user = result;
                    this.busy = false;

                }, () => {
                    this.busy = false;
                });
        } else {
            this.busy = false;
        }

        const verificationStatus = this.action ?? null;

        this.form = this.formBuilder.group({
            VerificationID: [this.userCycle.VerificationStage? this.userCycle.VerificationStage.VerificationID:0, []],
            StageID: [this.userCycle.VerificationStage? this.userCycle.VerificationStage.StageID:0, []],
            RefId: [this.userCycle.VerificationStage? this.userCycle.VerificationStage.RefId : this.userCycle.CycleID , []],
            VerificationStatus: [verificationStatus, [Validators.required]],
            ReopenStageId: [this.userCycle.VerificationStage? this.userCycle.VerificationStage.ReopenStageId: null, [this.requiredBasedOnStatusValidator(this.isStageIdRequired)]],
            VerifiedBy: [this.userCycle.VerificationStage? this.userCycle.VerificationStage.VerifiedBy: 0, []],
            VerifiedByName: [this.userCycle.VerificationStage? this.userCycle.VerificationStage.VerifiedByName: "", []],
            VerifiedDate: [this.userCycle.VerificationStage? this.userCycle.VerificationStage.VerifiedDate: new Date(), []],
            CompletedDate: [DateHelpers.isMinDate(this.userCycle.UserCompletedDate) ? null : this.userCycle.UserCompletedDate, [this.requiredBasedOnStatusValidator(this.isCompletedDateRequired)]],
            CompletedDateAsStart: [this.getCompletedDateAsStart(), [this.requiredBasedOnStatusValidator(this.isCompletedDateAsStartRequired)]],
            ShouldSubmitToPerms: [this.userCycle.ShouldSubmitToPerms ?? true, []],
            ShouldSendNotifications: [true, [Validators.required]],
            Notes: this.formBuilder.group({
                UserNote: ["", []],
                SupervisorNote: ["", []]

            })
        });

        this.form.get('VerificationStatus').valueChanges.subscribe(() => {
            this.updateValidity();
        });

        this.form.get('VerificationStatus').updateValueAndValidity();
        this.form.get('VerificationStatus').setValue(verificationStatus);
    }
    
    ok({ value, valid }: { value: UserVerification, valid: boolean}) {
        if (valid) {
            this.busy = true;

            if (value.VerificationStatus === VerificationStatus.SUBMIT) {
                this.submitForVerification(this.userCycle);
            } else {
                this.verify(value)
            }
        }
    }

    private updateValidity = () => {
        this.form.get('ReopenStageId').updateValueAndValidity({ onlySelf: true, emitEvent: false });
        this.form.get('CompletedDate').updateValueAndValidity({ onlySelf: true, emitEvent: false  });
        this.form.get('CompletedDateAsStart').updateValueAndValidity({ onlySelf: true , emitEvent: false });
    }

    private requiredBasedOnStatusValidator = (checkStatus: (status?: number | string) => boolean) => {
        return (formControl: AbstractControl) => {
            const parent = formControl.parent;
            const verificationStatus = parent?.get('VerificationStatus')?.value;
            return checkStatus(verificationStatus) ? Validators.required(formControl) : null;
        }
    }

    private getCompletedDateAsStart = () => {
        if (this.complianceItem.ItemType !== CycleType.ITEM && this.complianceItem.SubType === CplItemSubType.CLEARANCE) {
            return 1;
        }

        return this.userCycle.UseCompletedDateAsStart >= 0 ? this.userCycle.UseCompletedDateAsStart : null;
    }

    canPerformVerificationAction = (verificationStatus: VerificationStatus) => {
        const canPerformAction = this.isBulkAction() ?
            this.complianceItemHelpersService.canPerformBulkVerificationAction({
                CanManageItem: this.CanManageItem,
                IsVerifier: this.IsVerifier,
                complianceItem: this.complianceItem, 
                action: verificationStatus
            }) :
            this.complianceItemHelpersService.canPerformVerificationAction({
                CanManageItem: this.CanManageItem,
                cplCycle: this.userCycle, 
                complianceItem: this.complianceItem, 
                action: verificationStatus
            });

        if (!canPerformAction) return false;

        if (this.isBulkAction() && verificationStatus == VerificationStatus.APPROVED) {
            return this.complianceItemHelpersService.canBulkVerifyAllCycles(this.userCycleActions);
        }

        return true;
    }

    isStageIdRequired = (verificationStatus?: number | string) => {
        return verificationStatus == VerificationStatus.REOPEN;
    }

    isCompletedDateRequired = (verificationStatus?: number | string) => {
        return verificationStatus == VerificationStatus.APPROVED && (this.ItemHasCompletedDateRecurrence() || this.complianceItem.ItemType == CycleType.ITEM)
    }

    isCompletedDateAsStartRequired = (verificationStatus?: number | string) => {
        return verificationStatus == VerificationStatus.APPROVED && this.ItemHasCompletedDateRecurrence();
    }

    ItemHasCompletedDateRecurrence = () => {
        return this.complianceItem.ItemType == CycleType.USER && this.complianceItem.Recurrence!=null && this.complianceItem.Recurrence.RecurrencePattern != null;
    }

    getVerificationStatus = () => {
        return this.form.get('VerificationStatus').value;
    }

    private submitForVerification(userCycle: CplCycle) {
        this.verifyService.submitForVerification(userCycle)
            .subscribe((result) => this.onSubmissionSuccess({ successCount: 1, failCount: 0, result }), this.onSubmissionFail);
    }

    private verify(value: UserVerification) {
        const totalCount = this.isBulkAction() ? this.userCycleActions.length : 1;
        const actions = this.isBulkAction() ?
            this.generateBulkUserVerificationActions(value) :
            [ value ];
        
        const successCount = actions.length;
        const failCount = totalCount - successCount;

        this.verifyService
            .verify(actions)
            .subscribe((result) => this.onSubmissionSuccess({ successCount, failCount, result }), this.onSubmissionFail);
    }

    private generateBulkUserVerificationActions = (value: UserVerification) => {
        return this.userCycleActions
            .filter(v => this.complianceItemHelpersService.canPerformVerificationAction({
                CanManageItem: this.CanManageItem,
                cplCycle: v, 
                complianceItem: this.complianceItem, 
                action: value.VerificationStatus
            }))
            .map((userCycle: CplCycle) => {
                let cycleVerification = new UserVerification();
                cycleVerification.RefId = userCycle.CycleID;
                cycleVerification.VerificationStatus = value.VerificationStatus;
                cycleVerification.StageID = this.userCycle.VerificationStage ? this.userCycle.VerificationStage.StageID : "";
                cycleVerification.VerifiedDate = new Date();
                cycleVerification.ReopenStageId = value.ReopenStageId;
                cycleVerification.CompletedDate = value.CompletedDate;
                cycleVerification.CompletedDateAsStart = value.CompletedDateAsStart;
                cycleVerification.ShouldSubmitToPerms = userCycle.ShouldSubmitToPerms ?? true;
                cycleVerification.ShouldSendNotifications = value.ShouldSendNotifications;
                return cycleVerification;
            });
    }

    private onSubmissionSuccess = (value: { successCount: number, failCount: number, result: any }) => {
        this.busy = false;
        this.onSubmissionComplete.emit(true);

        if (this.canClose) {
            this.activeModal.close(value);
        }
    }

    private onSubmissionFail = (error: any) => {
        this.busy = false;
        console.log(error);
    }

    updateCompletedDateFlag($event, value){
        let targetElm = <HTMLInputElement>($event.srcElement||$event.target);
        const completedDateAsStart = targetElm.checked ? value : null;
        
        this.form.get('CompletedDateAsStart').setValue(completedDateAsStart) ;
        this.updateValidity();
    }

    isBulkAction = () => {
        return this.userCycleActions != null && this.userCycleActions.length > 0
    }

    shouldDisableStage = (stage: CplVerificationStage) => {
        return !this.isBulkAction() && (this.complianceItemHelpersService.isPendingSubmission(this.userCycle.UserStatus) || (this.pendingStage && stage.Order >= this.pendingStage.Order));
    }

    cancel() {
        this.activeModal.dismiss();
    }

    public canDisplayNotificationsToggle() {
        const status = this.getVerificationStatus();

        // Volunteers do not get notifications on approval or forgiven actions
        if (this.sessionService?.PortalSettings?.VolunteersEnabled 
            && (status == VerificationStatus.APPROVED || status == VerificationStatus.FORGIVEN)
        ) {
            return false;
        }

        return !(this.complianceItem?.Act48Detail?.Applicable ?? false) ||
            this.isBulkAction() || 
            status != VerificationStatus.APPROVED || 
            !this.isFinalStage;
    }

    public canDisplaySubmitHoursToggle() {
        const status = this.getVerificationStatus();
        return !this.isBulkAction() && this.user &&
            this.a48HelpersService.canDisplaySubmitHoursToggle({
                hasAct48: this.complianceItem?.Act48Detail?.Applicable ?? false,
                isUserPara: this.userCycle?.IsPara,
                hasPPID: !!this.user?.PPID,
                excludeFromSubmission: this.user?.ExcludeFromSubmission,
                contextCode: this.userCycle.A48ContextCode
            }) && (status.toString() == VerificationStatus.APPROVED.toString() || status.toString() === VerificationStatus.FORGIVEN.toString());
    }

    get completedDateControl() {
        return this.form?.get('CompletedDate');
    }

}